import { Candidate } from "@/types/candidate";
import { CandidateHeader } from "@/components/candidates/CandidateHeader";
import { ContactInformation } from "@/components/candidates/ContactInformation";
import { CandidateSummary } from "@/components/candidates/CandidateSummary";
import { CandidateEditTabs } from "@/components/candidates/CandidateEditTabs";
import { CandidateOverviewCard } from "@/components/candidates/CandidateOverviewCard";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FileSearch, FileText, Check, X } from "lucide-react";
import { CandidateAnalysisModal } from "@/components/candidates/CandidateAnalysisModal";
import { useTranslation } from "react-i18next";
import { CVFullScreenModal } from "@/components/candidates/CVFullScreenModal";
import { supabase } from "@/integrations/supabase/client";
import { SkillsList } from "@/components/candidates/SkillsList";
import { EntityEditForm } from "@/components/common/EntityEditForm";
import { useEntityEditForm } from "@/hooks/common/useEntityEditForm";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { useCandidateLanguages } from "@/hooks/languages/useCandidateLanguages";
import { CandidateLanguage } from "@/types/candidate";

interface CandidateProfileProps {
  candidate: Candidate;
  cv: string | null;
  cvExists: boolean;
  onCVUpdate: (newCVUrl: string) => void;
  isEditing: boolean;
  onChange: (candidate: Candidate) => void;
  onCandidateUpdate?: (updatedData: Partial<Candidate>) => Promise<void> | void;
  onStatusChanged?: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export const CandidateProfile = ({
  candidate,
  cv,
  cvExists,
  onCVUpdate,
  isEditing,
  onChange,
  onCandidateUpdate,
  onStatusChanged,
  onSave,
  onCancel
}: CandidateProfileProps) => {
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showCVModal, setShowCVModal] = useState(false);
  const [cvPreviewUrl, setCvPreviewUrl] = useState<string | null>(null);
  const [cvPreviewName, setCvPreviewName] = useState<string | null>(null);
  const [loadingCV, setLoadingCV] = useState(false);
  const { t } = useTranslation();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const { currentOrganization } = useAuth();
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);

  // Language management - fetched here so they are saved only on form save
  const { languages: candidateLanguages, loading: languagesLoading, updateLanguages } = useCandidateLanguages(candidate.id);
  const [pendingLanguages, setPendingLanguages] = useState<CandidateLanguage[]>([]);
  const [pendingLanguagesDirty, setPendingLanguagesDirty] = useState(false);

  // Sync fetched languages into pending state when they load (or when editing starts)
  useEffect(() => {
    if (!pendingLanguagesDirty)
      setPendingLanguages(candidateLanguages);
  }, [candidateLanguages]);

  // Reset pending state when editing is cancelled
  useEffect(() => {
    if (!isEditing) {
      setPendingLanguages(candidateLanguages);
      setPendingLanguagesDirty(false);
    }
  }, [isEditing]);

  const handleLanguagesChange = (updatedLanguages: any[]) => {
    const mapped: CandidateLanguage[] = updatedLanguages.map(lang => ({
      id: lang.id || '',
      candidate_id: candidate.id,
      language: lang.language,
      language_code: lang.language_code,
      cefr_level: lang.level,
      is_user_owned: lang.is_user_owned,
      created_at: lang.created_at || new Date().toISOString()
    }));
    setPendingLanguages(mapped);
    setPendingLanguagesDirty(true);
  };

  const handleSave = async () => {
    if (pendingLanguagesDirty)
      await updateLanguages(pendingLanguages);
    setPendingLanguagesDirty(false);
    onSave();
  };

  // Move the hook call to the top level to fix Rules of Hooks violation
  const editFormProps = useEntityEditForm({
    entityType: 'candidate',
    entity: candidate,
    onChange: (field, value) => onChange && onChange({ ...candidate, [field]: value })
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;
      const rect = bannerRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch custom fields configuration
  useEffect(() => {
    if (!currentOrganization?.id) return;
    setCustomFieldsLoading(true);
    setCustomFieldsError(null);
    fetchCustomFields(currentOrganization.id)
      .then((data: CustomFieldsJSON) => {
        setCustomFields(data.Candidates || []);
      })
      .catch(() => {
        setCustomFieldsError("Failed to load custom fields");
      })
      .finally(() => setCustomFieldsLoading(false));
  }, [currentOrganization?.id]);

  // Check if AI analysis exists and has at least one property
  const hasAiAnalysis = candidate.ai_analysis && Object.keys(candidate.ai_analysis).length > 0;

  // Fetch the default_resume (storage_id) and document display name, then get signed URL
  const handleViewCV = async () => {
    setLoadingCV(true);
    try {
      // 1. Get the candidate's default_resume (storage_id)
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select('default_resume')
        .eq('id', candidate.id)
        .single();
      if (candidateError || !candidateData?.default_resume) {
        throw new Error("No default CV found for candidate");
      }
      const storageId = candidateData.default_resume;

      // 2. Get the display name (optional)
      const { data: doc, error: docError } = await supabase
        .from('documents')
        .select('display_name, name')
        .eq('storage_id', storageId)
        .single();
      const displayName = doc?.display_name || doc?.name || "CV";

      // 3. Call the edge function using supabase.functions.invoke
      const { data, error } = await supabase.functions.invoke(`document-manager?command=get-document-url&documentStorageId=${storageId}`, {
        method: 'GET',
      });
      
      if (error) throw new Error(`Edge function error: ${error.message}`);
      if (!data?.signedUrl) throw new Error("No signed URL returned");
      
      let url = data.signedUrl;
      // Localhost dev fix
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        url = url.replace('http://kong:8000', 'http://localhost:54321');
      }
      setCvPreviewUrl(url);
      setCvPreviewName(displayName);
      setShowCVModal(true);
    } catch (err) {
      alert('Failed to load CV preview');
      setCvPreviewUrl(null);
      setShowCVModal(false);
    } finally {
      setLoadingCV(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {isEditing && (
        <StickyEditBanner
          isSticky={isSticky}
          onSave={handleSave}
          onCancel={onCancel}
        >
          {t('common.editMode', 'Edit mode')}
        </StickyEditBanner>
      )}
      <div className={`w-full border border-border p-6 ${isEditing ? 'ring-1 ring-blue-400 bg-blue-50/50 dark:bg-blue-950/30' : 'bg-card'}`}>
        {isEditing ? (
          <CandidateEditTabs
            candidate={candidate}
            isEditing={isEditing}
            onChange={(field, value) => onChange({ ...candidate, [field]: value })}
            customFieldsConfig={customFields}
            languages={pendingLanguages}
            onLanguagesChange={handleLanguagesChange}
            languagesLoading={languagesLoading}
          />
        ) : (
          <CandidateOverviewCard
            candidate={candidate}
            formattedDate={candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : ''}
            customFieldsConfig={customFields}
            cv={cv}
            cvExists={cvExists}
            onViewCV={handleViewCV}
            onCandidateUpdate={onCandidateUpdate}
            onStatusChanged={onStatusChanged}
          />
        )}
      </div>
      {/* AI Synthesis */}
      {candidate.ai_synthesis && (
        <div className="w-full border border-border p-6 bg-card">
          <h3 className="text-sm font-medium text-foreground mb-3">{t("candidates.profile.aiSummary")}</h3>
          <CandidateSummary summary={candidate.ai_synthesis} />
        </div>
      )}
      {/* CV Modal */}
      {showCVModal && cvPreviewUrl && (
        <CVFullScreenModal
          cvUrl={cvPreviewUrl}
          name={cvPreviewName || undefined}
          onClose={() => { setShowCVModal(false); setCvPreviewUrl(null); }}
        />
      )}
      {/* AI Analysis Modal */}
      {showAnalysisModal && (
        <CandidateAnalysisModal
          analysis={candidate.ai_analysis || {}}
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          candidateName={candidate.name}
          candidateFunction={candidate.function}
          candidateSynthesis={candidate.ai_synthesis}
        />
      )}
    </div>
  );
};
